/**
 * Store for learning path trainings
 */

import { defineStore } from "pinia";
import { LearningPathsUsersApi } from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IPaginationApi } from "~/utils/interfaces/table-interfaces";
import type { ILearningPathTrainingsStore } from "~/utils/interfaces/learning-path-interfaces";
import { LearningPathTrainingStatusEnum } from "~/utils/enums/learning-path.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useAuthStore } from "~/store/auth/auth.store";

export const useLearningPathTrainingsStore = defineStore("learningPathTrainingsStore", {
  state: (): ILearningPathTrainingsStore => ({
    currentTrainingPathId: null,
    trainingsDatas: null,
    learningPathTrainingsAttendeesFullDatas: null as IPaginationApi<any> | null,
    learningPathTrainingsAttendeesMultiSelect: {
      selectedItems: [],
      excludedItems: [],
    },
    learningPathTrainingsAttendeesSelectAll: false,
    learningPathTrainingsSessionsModal: false,
    sessionsDatas: [],
    shouldCloseCollapse: false,
    pollingIntervalId: null,
    pollingCallCount: 0,
  }),
  actions: {
    handleLearningPathTrainingsSessionsModal(value: boolean) {
      this.learningPathTrainingsSessionsModal = value;
    },
    setLearningPathTrainings(learningPathTrainings: any) {
      this.trainingsDatas = learningPathTrainings;
    },
    setCurrentTrainingPathId(trainingPathId: string | null) {
      this.currentTrainingPathId = trainingPathId;
    },
    setLearningPathTrainingsAttendeesFullDatas(data: IPaginationApi<any> | null) {
      this.learningPathTrainingsAttendeesFullDatas = data;
    },
    setSessionsDatas(sessions: any[]) {
      this.sessionsDatas = sessions;
    },
    resetSessionsCheck() {
      this.sessionsDatas.forEach((session: any) => {
        session.isCheck = false;
      });
    },
    setShouldCloseCollapse(value: boolean) {
      this.shouldCloseCollapse = value;
    },
    async fetchLearningPathTrainings(learningPathId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        const data = await apiInstance.findAllLearningPathTrainingsInscriptions({
          learningPathId,
        });

        this.setLearningPathTrainings(data);
      } catch {
        this.setLearningPathTrainings(null);
      }
    },
    async fetchLearningPathTrainingsAttendeesFullDatas(
      trainingPathId: string,
      paginationOptions?: any,
      context?: string,
    ) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        const data = await apiInstance.findAllUsersNotBookedInAlearningPathTraining({
          learningPathTrainingId: trainingPathId,
          ...paginationOptions,
          context,
        });
        this.setLearningPathTrainingsAttendeesFullDatas({
          data: data.data,
          meta: {
            count: data.meta?.count ?? 0,
            totalCount: null,
          },
        });
      } catch {
        this.setLearningPathTrainingsAttendeesFullDatas(null);
      }
    },
    resetLearningPathTrainingsAttendeesMultiSelect() {
      if (this.learningPathTrainingsAttendeesMultiSelect) {
        this.learningPathTrainingsAttendeesMultiSelect.selectedItems = [];
        this.learningPathTrainingsAttendeesMultiSelect.excludedItems = [];
      }
      this.learningPathTrainingsAttendeesSelectAll = false;
    },
    async confirmAttendeesToSession(sessionId: string | null) {
      const route = useRoute();
      const learningPathId = (route.query.learning_path_id as string) || null;
      const feedback = useFeedback();
      const authStore = useAuthStore();

      if (!this.currentTrainingPathId) {
        feedback.error("Aucune formation sélectionnée", "small");
        return;
      }

      const companyId = authStore.user?.mainEntityId?.companyId;
      if (!companyId) {
        feedback.error("Aucune entreprise trouvée", "small");
        return;
      }

      try {
        let userIds: string[];
        if (this.learningPathTrainingsAttendeesSelectAll) {
          userIds =
            this.learningPathTrainingsAttendeesMultiSelect?.excludedItems?.map((item: any) => item.userId) ?? [];
        } else {
          userIds =
            this.learningPathTrainingsAttendeesMultiSelect?.selectedItems?.map((item: any) => item.userId) ?? [];
        }

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        await apiInstance.bookLearningPathUsers({
          bookLearningPathUsersDto: {
            ...(sessionId && { sessionId }),
            userIds,
            companyId,
            selectAll: this.learningPathTrainingsAttendeesSelectAll,
            learningPathTrainingId: this.currentTrainingPathId,
          },
        });

        feedback.waiting(
          `Les participants sont en cours d'inscription à la session, cela peut prendre quelques minutes.`,
          "small",
        );

        this.setShouldCloseCollapse(true);
        this.handleLearningPathTrainingsSessionsModal(false);

        this.resetLearningPathTrainingsAttendeesMultiSelect();
        if (learningPathId) {
          await this.cronFetchTrainings(learningPathId);
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de l'inscription`, "small");
      }
    },
    /**
     * Polling automatically to refresh the data of the trainings in registration
     *
     * Functioning :
     * 1. Fetch the status of the trainings to get the IDs of the trainings in registration
     * 2. If there are trainings in registration, fetch the complete data and update the store
     * 3. Repeat the operation automatically every 10 seconds
     * 4. Each new call cancels the previous polling and starts a new one
     *
     * Automatic stop :
     * - When there are no trainings in registration (empty return), fetch the last time the data for the trainings in registration
     * - Via the manual call to stopCronFetchTrainings()
     */
    async cronFetchTrainings(learningPathId: string) {
      this.stopCronFetchTrainings();
      this.pollingCallCount = 0;

      // The first poll runs right after a booking is triggered (confirmAttendeesToSession),
      // before the backend has flipped the training status to *_bookings. Don't let that
      // first result — still "active", or a transient error — tear the interval down before
      // it can observe the change.
      let isInitialFetch = true;
      const fetchStatus = async () => {
        const initial = isInitialFetch;
        isInitialFetch = false;
        try {
          this.pollingCallCount++;

          await this.fetchLearningPathTrainings(learningPathId);

          const hasTrainingsInProgress = this.trainingsDatas?.some(
            (training: any) =>
              training.status !== LearningPathTrainingStatusEnum.ACTIVE &&
              training.status !== LearningPathTrainingStatusEnum.ERROR,
          );
          if (!hasTrainingsInProgress && !initial) {
            this.stopCronFetchTrainings();
          }
        } catch {
          if (!initial) {
            this.stopCronFetchTrainings();
          }
        }
      };

      // Arm the interval synchronously, before the first await, so a stopCronFetchTrainings()
      // from modal close / unmount during that first request clears it — instead of the
      // pending call re-arming polling after cleanup has already run.
      this.pollingIntervalId = setInterval(fetchStatus, 10000);
      await fetchStatus();
    },

    stopCronFetchTrainings() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
        this.pollingCallCount = 0;
      }
    },
  },
});
